import { MAP_SIZE } from './pipeline';
import {
  NEUTRAL_ADJUSTMENTS,
  prepCacheKey,
  prepareImage,
  type ImageAdjustments,
} from './imagePrep';
import { FOLLOW_BUILD, FOLLOW_BUILD_SECTION, type BuildSection, type SectionMap } from './types';

/** Every layer is a build section of its own: it can dither and staircase apart from the rest. */
export interface LayerBase extends BuildSection {
  id: string;
  name: string;
  visible: boolean;
  opacity: number;
  x: number;
  y: number;
  scale: number;
  rotation: number;
  /** Set on every member of a group. Absent on a layer that stands on its own. */
  groupId?: string;
}

export interface ImageLayer extends LayerBase, ImageAdjustments {
  kind: 'image';
  sourceId: string;
  width: number;
  height: number;
  /**
   * Nearest neighbour sampling, whole block positions, quarter turns, and only the scales that keep
   * every source pixel a whole square of blocks: n times, or every nth pixel when shrinking.
   */
  pixelPerfect: boolean;
}

/** Same ceiling the resize handles clamp to. */
export const MAX_LAYER_SCALE = 64;

function greatestCommonDivisor(left: number, right: number): number {
  return right === 0 ? left : greatestCommonDivisor(right, left % right);
}

/** Shrinking by 1/n only drops whole pixels when n divides both sides. */
function pixelDivisors(width: number, height: number): number[] {
  const shared = greatestCommonDivisor(Math.round(width), Math.round(height));
  const divisors: number[] = [];
  for (let candidate = 1; candidate <= shared; candidate++) {
    if (shared % candidate === 0) divisors.push(candidate);
  }
  return divisors;
}

export function snapPixelScale(scale: number, width: number, height: number): number {
  if (scale >= 1) return Math.min(MAX_LAYER_SCALE, Math.max(1, Math.round(scale)));
  const wanted = Math.log(1 / scale);
  const divisor = pixelDivisors(width, height).reduce((best, candidate) =>
    Math.abs(Math.log(candidate) - wanted) < Math.abs(Math.log(best) - wanted) ? candidate : best,
  );
  return 1 / divisor;
}

/** Largest pixel perfect scale that still fits the given box, for placing a new image. */
export function fittingPixelScale(width: number, height: number, maxWidth: number, maxHeight: number): number {
  const fit = Math.min(maxWidth / width, maxHeight / height);
  if (fit >= 1) return Math.min(MAX_LAYER_SCALE, Math.floor(fit));
  const divisors = pixelDivisors(width, height);
  const fitting = divisors.find((divisor) => width / divisor <= maxWidth && height / divisor <= maxHeight);
  return 1 / (fitting ?? divisors[divisors.length - 1]);
}

/** Pulls a pixel perfect image back onto its grid after any edit; leaves every other layer alone. */
export function settlePixelPerfect(layer: Layer): void {
  if (layer.kind !== 'image' || !layer.pixelPerfect) return;
  layer.scale = snapPixelScale(layer.scale, layer.width, layer.height);
  layer.x = Math.round(layer.x);
  layer.y = Math.round(layer.y);
  const quarters = Math.round(layer.rotation / 90);
  layer.rotation = (((quarters % 4) + 5) % 4) * 90 - 90;
}

export type PaintKind = 'none' | 'solid' | 'linear' | 'radial';

export interface GradientStop {
  offset: number;
  colour: string;
}

export interface Paint {
  kind: PaintKind;
  colour: string;
  stops: GradientStop[];
  /** Linear gradient direction in degrees, 0 pointing right. */
  angle: number;
}

export type ShapeKind = 'rect' | 'ellipse' | 'triangle' | 'line';

/**
 * Canvas always antialiases glyphs, and every part transparent edge pixel quantises to a different
 * block, which is how a white word ends up built from white plus two greys. `hard` cuts each pixel
 * to fully on or fully off before the palette ever sees it.
 */
export type TextEdges = 'smooth' | 'hard';

/** What every drawn, non image layer carries: how its edges land. */
export interface VectorOptions {
  edges: TextEdges;
  /** Coverage a pixel needs to survive `hard` edges: lower keeps more, so shapes read fatter. */
  edgeCutoff: number;
}

export interface TextLayer extends LayerBase, VectorOptions {
  kind: 'text';
  text: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: number;
  fill: Paint;
  stroke: Paint;
  strokeWidth: number;
  /**
   * Pixel fonts only. On, the size rounds to a whole number of blocks per font pixel, which is the
   * only way every stroke comes out the same width. Off, the size is taken literally and strokes
   * land on whichever side of a block boundary they fall, so some are a block wider than others.
   */
  pixelSnap: boolean;
  /** Pixel fonts only: keep the font's fixed cell instead of the proportional widths the game uses. */
  monospace: boolean;
}

export const DEFAULT_TEXT_EDGES: TextEdges = 'hard';
export const DEFAULT_EDGE_CUTOFF = 0.5;
export const DEFAULT_FONT_WEIGHT = 400;

/**
 * The Minecraft font ships a single face, and the game has no bold file either: it draws the glyph a
 * second time one pixel to the right. Emulating that is both the only way to get weight out of this
 * font and what the built map will actually look like in game.
 */
export const MINECRAFT_FONT_FAMILY = "'Minecraft', monospace";

/** Bold from this weight up, matching how a browser splits the scale. */
export const BOLD_FROM = 600;

/**
 * Font size at which one Minecraft font pixel is one canvas pixel, measured off the shipped face: a
 * character advances 6 pixels there. The face is an unhinted outline, so canvas antialiases it at
 * every size, which is why the pixel path rasterises oversized and averages instead of drawing text
 * at this size directly.
 */
export const MINECRAFT_CELL = 9;

export function isPixelFont(fontFamily: string): boolean {
  return fontFamily === MINECRAFT_FONT_FAMILY;
}

/** Nearest size landing on whole font pixels, so a control can show what actually gets drawn. */
export function snapFontSize(size: number): number {
  return Math.max(1, Math.round(size / MINECRAFT_CELL)) * MINECRAFT_CELL;
}

/** Size the text is drawn at, rounded onto the font's own grid only when snapping is asked for. */
export function effectiveFontSize(layer: TextLayer): number {
  const requested = layer.fontSize * layer.scale;
  if (!isPixelFont(layer.fontFamily) || layer.pixelSnap === false) return requested;
  return snapFontSize(requested);
}

/**
 * Blocks per font pixel. Whole while snapping, so every stroke is the same width; fractional
 * otherwise, which is what lets the size be taken literally.
 */
export function pixelBlockScale(layer: TextLayer): number {
  return Math.max(1 / MINECRAFT_CELL, effectiveFontSize(layer) / MINECRAFT_CELL);
}

/** What the panel reports: how wide a stroke ends up, rounded for display. */
export function pixelStrokeBlocks(layer: TextLayer): number {
  return Math.max(1, Math.round(pixelBlockScale(layer)));
}

/**
 * Text is flat colour by nature, so dithering it only adds stray shades around the letters. A shape
 * can carry a gradient, which dithering genuinely helps, so it follows the build like a photo does.
 */
export const DEFAULT_LAYER_DITHER: Record<'image' | 'text' | 'shape', string> = {
  image: FOLLOW_BUILD,
  text: 'none',
  shape: FOLLOW_BUILD,
};

export function defaultLayerSection(kind: 'image' | 'text' | 'shape'): BuildSection {
  return { ...FOLLOW_BUILD_SECTION, dither: DEFAULT_LAYER_DITHER[kind] };
}

/** The one place the canvas font string is built, so measuring and drawing cannot drift apart. */
export function textFont(layer: TextLayer): string {
  return `${layer.fontWeight ?? DEFAULT_FONT_WEIGHT} ${effectiveFontSize(layer)}px ${layer.fontFamily}`;
}

export interface ShapeLayer extends LayerBase, VectorOptions {
  kind: 'shape';
  shape: ShapeKind;
  width: number;
  height: number;
  radius: number;
  fill: Paint;
  stroke: Paint;
  strokeWidth: number;
}

export type Layer = ImageLayer | TextLayer | ShapeLayer;

/**
 * Every `LayerBase` field, so a patch across a mixed selection can tell what applies to all layers
 * from what only makes sense for one kind.
 */
const UNIVERSAL_LAYER_KEYS = new Set<string>([
  'name',
  'visible',
  'opacity',
  'x',
  'y',
  'scale',
  'rotation',
  'dither',
  'ditherStrength',
  'staircase',
]);

export function isUniversalPatch(patch: Partial<Layer>): boolean {
  return Object.keys(patch).every((key) => UNIVERSAL_LAYER_KEYS.has(key));
}

export function solidPaint(colour: string): Paint {
  return { kind: 'solid', colour, stops: [], angle: 0 };
}

export function noPaint(): Paint {
  return { kind: 'none', colour: '#000000', stops: [], angle: 0 };
}

export function gradientPaint(kind: 'linear' | 'radial', from: string, to: string): Paint {
  return {
    kind,
    colour: from,
    stops: [
      { offset: 0, colour: from },
      { offset: 1, colour: to },
    ],
    angle: 90,
  };
}

export interface MapartDocument {
  mapsX: number;
  mapsZ: number;
  background: string;
  layers: Layer[];
}

export const TRANSPARENT = 'transparent';

let idCounter = 0;
export function nextLayerId(): string {
  idCounter += 1;
  return `l${idCounter}`;
}

/** Called after loading a project so new layers cannot reuse an id the document already holds. */
export function reserveLayerIds(upTo: number) {
  idCounter = Math.max(idCounter, upTo);
}

/**
 * Grouping is a tag on the members, not a nesting level, so the layer list stays flat and neither
 * compositing nor the schematic writer has to know groups exist. What a group buys is that its
 * members are selected, moved, reordered, hidden and deleted as one, and that they sit together in
 * the stack.
 */
export function newGroupId(): string {
  return crypto.randomUUID();
}

/** The given ids plus every other member of the groups they touch, in stacking order. */
export function expandGroups(layers: Layer[], ids: string[]): string[] {
  const wanted = new Set(ids);
  const groups = new Set(
    layers.flatMap((layer) => (wanted.has(layer.id) && layer.groupId ? [layer.groupId] : [])),
  );
  return layers
    .filter((layer) => wanted.has(layer.id) || (layer.groupId !== undefined && groups.has(layer.groupId)))
    .map((layer) => layer.id);
}

function withoutGroup(layer: Layer): Layer {
  if (layer.groupId === undefined) return layer;
  const copy = { ...layer };
  delete copy.groupId;
  return copy;
}

/** Members keep their relative order but end up adjacent, so one group is one block in the stack. */
function gather(layers: Layer[], members: Set<string>): Layer[] {
  const block = layers.filter((layer) => members.has(layer.id));
  const rest = layers.filter((layer) => !members.has(layer.id));
  const topmost = layers.reduce((found, layer, index) => (members.has(layer.id) ? index : found), 0);
  const above = layers.slice(topmost + 1).filter((layer) => !members.has(layer.id)).length;
  const cut = rest.length - above;
  return [...rest.slice(0, cut), ...block, ...rest.slice(cut)];
}

export function groupLayers(layers: Layer[], ids: string[]): Layer[] {
  const members = new Set(expandGroups(layers, ids));
  if (members.size < 2) return layers;
  const groupId = newGroupId();
  return gather(
    layers.map((layer) => (members.has(layer.id) ? { ...layer, groupId } : layer)),
    members,
  );
}

/** Dissolves every group the given ids belong to, leaving the layers themselves untouched. */
export function ungroupLayers(layers: Layer[], ids: string[]): Layer[] {
  const wanted = new Set(ids);
  const groups = new Set(
    layers.flatMap((layer) => (wanted.has(layer.id) && layer.groupId ? [layer.groupId] : [])),
  );
  if (groups.size === 0) return layers;
  return layers.map((layer) =>
    layer.groupId !== undefined && groups.has(layer.groupId) ? withoutGroup(layer) : layer,
  );
}

/** A group of one is not a group. Leftovers appear once the rest of a group is deleted. */
export function pruneGroups(layers: Layer[]): Layer[] {
  const counts = new Map<string, number>();
  for (const layer of layers) {
    if (layer.groupId === undefined) continue;
    counts.set(layer.groupId, (counts.get(layer.groupId) ?? 0) + 1);
  }
  return layers.map((layer) =>
    layer.groupId !== undefined && (counts.get(layer.groupId) ?? 0) < 2 ? withoutGroup(layer) : layer,
  );
}

/** Copies need groups of their own, or a pasted group silently joins the one it was copied from. */
export function remapGroups(layers: Layer[]): Layer[] {
  const fresh = new Map<string, string>();
  return layers.map((layer) => {
    if (layer.groupId === undefined) return layer;
    const mapped = fresh.get(layer.groupId) ?? newGroupId();
    fresh.set(layer.groupId, mapped);
    return { ...layer, groupId: mapped };
  });
}

/**
 * Moves a layer, or the whole group it belongs to, past its neighbour. A grouped neighbour is
 * stepped over as a block too, so reordering can never interleave two groups.
 */
export function reorderBlock(layers: Layer[], ids: string[], direction: -1 | 1): Layer[] {
  const block = new Set(expandGroups(layers, ids));
  if (block.size === 0) return layers;
  const indices = layers.flatMap((layer, index) => (block.has(layer.id) ? [index] : []));
  const neighbour = direction === 1 ? indices[indices.length - 1] + 1 : indices[0] - 1;
  if (neighbour < 0 || neighbour >= layers.length) return layers;

  const neighbourBlock = expandGroups(layers, [layers[neighbour].id]);
  const moving = layers.filter((layer) => block.has(layer.id));
  const rest = layers.filter((layer) => !block.has(layer.id));
  const edge = direction === 1 ? neighbourBlock[neighbourBlock.length - 1] : neighbourBlock[0];
  const anchor = rest.findIndex((layer) => layer.id === edge);
  const at = direction === 1 ? anchor + 1 : anchor;
  return [...rest.slice(0, at), ...moving, ...rest.slice(at)];
}

export function emptyDocument(): MapartDocument {
  return { mapsX: 1, mapsZ: 1, background: TRANSPARENT, layers: [] };
}

export function documentWidth(doc: MapartDocument): number {
  return doc.mapsX * MAP_SIZE;
}

export function documentLength(doc: MapartDocument): number {
  return doc.mapsZ * MAP_SIZE;
}

/** Drawn layers used to carry `dither` as an on/off switch, and no layer carried a section at all. */
function normaliseSection(layer: Layer): BuildSection {
  const stored = layer as Partial<Omit<BuildSection, 'dither'>> & { dither?: string | boolean };
  const dither =
    stored.dither === true
      ? FOLLOW_BUILD
      : stored.dither === false
        ? 'none'
        : (stored.dither ?? DEFAULT_LAYER_DITHER[layer.kind]);
  return {
    dither,
    ditherStrength: stored.ditherStrength ?? FOLLOW_BUILD_SECTION.ditherStrength,
    staircase: stored.staircase ?? FOLLOW_BUILD,
  };
}

/** Text layers used to carry a single `colour`; fold those into the paint model on load. */
export function normaliseDocument(doc: MapartDocument): MapartDocument {
  return {
    ...doc,
    layers: doc.layers.map((stored) => {
      const layer = { ...stored, ...normaliseSection(stored) } as Layer;
      if (layer.kind === 'image') return { ...NEUTRAL_ADJUSTMENTS, ...layer, pixelPerfect: layer.pixelPerfect ?? false };
      if (layer.kind !== 'text' && layer.kind !== 'shape') return layer;
      const legacy = layer as TextLayer & { colour?: string };
      const painted = {
        ...layer,
        fill: layer.fill ?? solidPaint(legacy.colour ?? '#ffffff'),
        stroke: layer.stroke ?? noPaint(),
        strokeWidth: layer.strokeWidth ?? 0,
        edges: layer.edges ?? DEFAULT_TEXT_EDGES,
        edgeCutoff: layer.edgeCutoff ?? DEFAULT_EDGE_CUTOFF,
      };
      if (painted.kind !== 'text') return painted;
      return {
        ...painted,
        fontWeight: painted.fontWeight ?? DEFAULT_FONT_WEIGHT,
        pixelSnap: painted.pixelSnap ?? true,
        monospace: painted.monospace ?? false,
      };
    }),
  };
}

/**
 * Artwork space bounds of a layer, needed for hit testing, snapping and for centring a new one.
 * `x`/`y` are offsets from the layer origin, negative when a stroke widens the box: canvas centres
 * a stroke on the path, so half of it falls outside the shape's own width and height.
 */
export function layerBox(
  layer: Layer,
  measureText: (layer: TextLayer) => number,
): { x: number; y: number; width: number; height: number } {
  const content =
    layer.kind === 'text'
      ? { width: measureText(layer), height: effectiveFontSize(layer) }
      : { width: layer.width * layer.scale, height: layer.height * layer.scale };

  if (layer.kind === 'image' || !(layer.strokeWidth > 0) || layer.stroke?.kind === 'none') {
    return { x: 0, y: 0, ...content };
  }

  const bleed = (layer.strokeWidth * layer.scale) / 2;
  return {
    x: -bleed,
    y: -bleed,
    width: content.width + bleed * 2,
    height: content.height + bleed * 2,
  };
}

export class ImageRegistry {
  private readonly sources = new Map<string, { bitmap: ImageBitmap; blob: Blob }>();
  private readonly prepared = new Map<string, { key: string; canvas: HTMLCanvasElement }>();

  set(id: string, bitmap: ImageBitmap, blob: Blob) {
    this.sources.get(id)?.bitmap.close();
    this.sources.set(id, { bitmap, blob });
  }

  get(id: string): ImageBitmap | undefined {
    return this.sources.get(id)?.bitmap;
  }

  /** Original bytes per source, for saving or exporting the project. */
  blobs(): Record<string, Blob> {
    return Object.fromEntries([...this.sources].map(([id, entry]) => [id, entry.blob]));
  }

  /** One prepared canvas per source, rebuilt only when its size or adjustments change. */
  preparedFor(
    id: string,
    width: number,
    height: number,
    adjustments: ImageAdjustments,
  ): HTMLCanvasElement | undefined {
    const bitmap = this.sources.get(id)?.bitmap;
    if (!bitmap) return undefined;
    const key = prepCacheKey(id, Math.round(width), Math.round(height), adjustments);
    const cached = this.prepared.get(id);
    if (cached?.key === key) return cached.canvas;
    const canvas = prepareImage(bitmap, width, height, adjustments);
    this.prepared.set(id, { key, canvas });
    return canvas;
  }

  remove(id: string) {
    this.sources.get(id)?.bitmap.close();
    this.sources.delete(id);
    this.prepared.delete(id);
  }

  clear() {
    for (const entry of this.sources.values()) entry.bitmap.close();
    this.sources.clear();
    this.prepared.clear();
  }
}

const isDrawn = (layer: Layer) => layer.visible && layer.opacity > 0;

function sameSection(left: BuildSection, right: BuildSection): boolean {
  return (
    left.dither === right.dither &&
    left.staircase === right.staircase &&
    (left.dither === FOLLOW_BUILD || left.ditherStrength === right.ditherStrength)
  );
}

/** `SectionMap.owners` is one byte per pixel. */
const MAX_SECTIONS = 256;

/** Equal sections share an entry, so the table stays as short as the distinct settings in use. */
function sectionIndex(map: SectionMap, layer: BuildSection): number {
  const found = map.sections.findIndex((section) => sameSection(section, layer));
  if (found >= 0) return found;
  // Only reachable past 255 distinct combinations; the layer then builds by the settings.
  if (map.sections.length >= MAX_SECTIONS) return 0;
  map.sections.push({ dither: layer.dither, ditherStrength: layer.ditherStrength, staircase: layer.staircase });
  return map.sections.length - 1;
}

export function composite(
  doc: MapartDocument,
  registry: ImageRegistry,
  ctx: CanvasRenderingContext2D,
  /** Filled with the section owning each pixel: the topmost layer drawing there. Left alone when omitted. */
  sections?: SectionMap | null,
): void {
  const width = documentWidth(doc);
  const length = documentLength(doc);
  ctx.clearRect(0, 0, width, length);
  if (sections) {
    sections.owners.fill(0);
    sections.sections.length = 0;
    sections.sections.push(FOLLOW_BUILD_SECTION);
  }
  if (doc.background !== TRANSPARENT) {
    ctx.fillStyle = doc.background;
    ctx.fillRect(0, 0, width, length);
  }

  // Below the first layer with a section of its own every pixel is section 0 already, so only the
  // layers from there up have to be read back. Above it even a following layer must mark its
  // pixels, or it would leave them to whatever it covers.
  const firstMarked = sections
    ? doc.layers.findIndex((layer) => isDrawn(layer) && !sameSection(layer, FOLLOW_BUILD_SECTION))
    : -1;

  doc.layers.forEach((layer, layerIndex) => {
    if (!isDrawn(layer)) return;
    ctx.save();
    ctx.globalAlpha = Math.min(1, Math.max(0, layer.opacity));

    const hardEdges = layer.kind !== 'image' && layer.edges === 'hard';
    const marks = sections && firstMarked >= 0 && layerIndex >= firstMarked ? sections : null;
    if (hardEdges || marks) {
      // Every one of these needs the layer alone on a surface first: reworking what is already
      // composited would cut into whatever sits underneath, and the section map has to know which
      // pixels are this layer's rather than the stack's.
      const scratch = scratchContext(width, length);
      if (scratch) {
        drawLayer(scratch, layer, registry);
        // Whole surface, not the layer box: a rotation, a wide stroke or a glyph overshoot puts ink
        // outside any box we could compute, and anything left unthresholded shows up as grey blocks.
        const region = { x: 0, y: 0, width, height: length };
        if (hardEdges) hardenAlpha(scratch, region, layer.edgeCutoff);
        ctx.drawImage(scratch.canvas, 0, 0);
        if (marks) markOwned(scratch, marks.owners, sectionIndex(marks, layer), width, length);
      }
    } else {
      drawLayer(ctx, layer, registry);
    }

    ctx.restore();
  });
}

function markOwned(
  ctx: CanvasRenderingContext2D,
  owners: Uint8Array,
  section: number,
  width: number,
  height: number,
) {
  const data = ctx.getImageData(0, 0, width, height).data;
  for (let p = 0; p < owners.length; p++) {
    if (data[p * 4 + 3] > 0) owners[p] = section;
  }
}

function drawLayer(ctx: CanvasRenderingContext2D, layer: Layer, registry: ImageRegistry) {
  if (layer.kind !== 'image') {
    drawVector(ctx, layer);
    return;
  }
  const w = layer.width * layer.scale;
  const h = layer.height * layer.scale;
  const adjustments = layer.pixelPerfect ? { ...layer, resample: 'pixel' as const } : layer;
  const prepared = registry.preparedFor(layer.sourceId, w, h, adjustments);
  if (!prepared) return;
  // The scratch surface is reused, so the transform and smoothing this sets must not outlive it.
  ctx.save();
  if (layer.pixelPerfect) {
    drawOnGrid(ctx, layer, prepared);
  } else {
    applyTransform(ctx, layer, w, h);
    ctx.drawImage(prepared, 0, 0);
  }
  ctx.restore();
}

function drawVector(ctx: CanvasRenderingContext2D, layer: TextLayer | ShapeLayer) {
  if (layer.kind === 'text') {
    drawText(ctx, layer);
    return;
  }
  const w = layer.width * layer.scale;
  const h = layer.height * layer.scale;
  ctx.save();
  applyTransform(ctx, layer, w, h);
  tracePath(ctx, layer, w, h);
  if (layer.shape !== 'line') paintFill(ctx, layer.fill, w, h, () => ctx.fill());
  paintStroke(ctx, layer, w, h, () => ctx.stroke());
  ctx.restore();
}

/** How far a second pass sits from the first to fake bold, scaled off the font's glyph cell. */
export function pixelBoldOffset(layer: TextLayer): number {
  if (!isPixelFont(layer.fontFamily) || layer.fontWeight < BOLD_FROM) return 0;
  return pixelStrokeBlocks(layer);
}

/**
 * Font pixels per glyph cell, and how far above the em box the shipped face draws.
 */
const MINECRAFT_GLYPH = { advance: 6, height: 9 };

/** Rasterise at this multiple of a font pixel, then read each cell back down to one. */
const SUPERSAMPLE = 8;

let tile: HTMLCanvasElement | null = null;

/**
 * The shipped webfont is an outline face with no hinting, so canvas antialiases it at every size:
 * a stroke that should be one pixel lands as two half covered columns, which then thresholds to two
 * blocks or to none depending on where the text sits. Rasterising it eight times oversized and
 * averaging each cell back to a single pixel recovers the bitmap the font is drawn from, and that
 * blits at any whole scale with strokes exactly one block per step.
 */
interface PixelBitmap {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
}

/** One pixel of air after each glyph, and the width the game gives a blank. */
const GLYPH_SPACING = 1;
const SPACE_ADVANCE = 4;

interface GlyphMask {
  /** Ink only, `width` columns by the glyph height. */
  cells: Uint8Array;
  width: number;
  /** Columns to step before the next glyph when the text is proportional. */
  advance: number;
}

let glyphTile: HTMLCanvasElement | null = null;
const glyphCache = new Map<string, GlyphMask>();

/**
 * The bitmap of one glyph, its cell grid aligned to the glyph's own ink. Every glyph sits at its own
 * sub pixel offset inside its advance box, so this is searched per glyph rather than per string: the
 * offset that suits an `o` leaves an `i`, whose stem is a single column, split across two cells.
 */
function alignedGlyphMask(glyph: string, bold: number, cutoff: number): GlyphMask {
  const key = `${glyph} ${bold} ${cutoff}`;
  const hit = glyphCache.get(key);
  if (hit !== undefined) return hit;

  const advance = MINECRAFT_GLYPH.advance + bold;
  const rows = MINECRAFT_GLYPH.height;
  if (!glyphTile) glyphTile = document.createElement('canvas');
  const surface = glyphTile;
  // Padded by a cell each side so a glyph reaching past its advance box is not clipped by the search.
  const pad = SUPERSAMPLE;
  surface.width = advance * SUPERSAMPLE + pad * 2;
  surface.height = rows * SUPERSAMPLE + pad * 2;
  const ctx = surface.getContext('2d', { willReadFrequently: true });
  const blank = { cells: new Uint8Array(0), width: 0, advance: SPACE_ADVANCE + bold };
  if (!ctx) return blank;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, surface.width, surface.height);
  ctx.font = `400 ${MINECRAFT_CELL * SUPERSAMPLE}px ${MINECRAFT_FONT_FAMILY}`;
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#ffffff';
  ctx.fillText(glyph, pad, pad);
  if (bold) ctx.fillText(glyph, pad + bold * SUPERSAMPLE, pad);

  const data = ctx.getImageData(0, 0, surface.width, surface.height).data;
  const stride = surface.width + 1;
  const integral = new Float64Array(stride * (surface.height + 1));
  for (let y = 0; y < surface.height; y++) {
    for (let x = 0; x < surface.width; x++) {
      integral[(y + 1) * stride + x + 1] =
        data[(y * surface.width + x) * 4 + 3] / 255 +
        integral[y * stride + x + 1] +
        integral[(y + 1) * stride + x] -
        integral[y * stride + x];
    }
  }
  const cellAt = (px: number, py: number) => {
    const x0 = Math.max(0, Math.min(surface.width, px));
    const y0 = Math.max(0, Math.min(surface.height, py));
    const x1 = Math.max(0, Math.min(surface.width, px + SUPERSAMPLE));
    const y1 = Math.max(0, Math.min(surface.height, py + SUPERSAMPLE));
    const area = (x1 - x0) * (y1 - y0);
    if (area <= 0) return 0;
    return (
      (integral[y1 * stride + x1] -
        integral[y0 * stride + x1] -
        integral[y1 * stride + x0] +
        integral[y0 * stride + x0]) /
      area
    );
  };

  // The offset whose cells are least ambiguous is the one sitting on the glyph's own pixels.
  let best = { x: pad, y: pad, score: -Infinity };
  for (let oy = 0; oy < SUPERSAMPLE; oy++) {
    for (let ox = 0; ox < SUPERSAMPLE; ox++) {
      let score = 0;
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < advance; x++) {
          score += Math.abs(cellAt(pad + x * SUPERSAMPLE + ox, pad + y * SUPERSAMPLE + oy) - 0.5);
        }
      }
      if (score > best.score) best = { x: pad + ox, y: pad + oy, score };
    }
  }

  const full = new Uint8Array(advance * rows);
  let left = advance;
  let right = -1;
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < advance; x++) {
      if (cellAt(best.x + x * SUPERSAMPLE, best.y + y * SUPERSAMPLE) < cutoff) continue;
      full[y * advance + x] = 1;
      if (x < left) left = x;
      if (x > right) right = x;
    }
  }

  // Trimmed to its own ink, which is what makes this proportional: the game gives every glyph the
  // width it actually occupies plus one pixel of spacing, so a dot takes two columns and not six.
  if (right < 0) {
    glyphCache.set(key, blank);
    return blank;
  }
  const width = right - left + 1;
  const cells = new Uint8Array(width * rows);
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < width; x++) cells[y * width + x] = full[y * advance + left + x];
  }
  const trimmed = { cells, width, advance: width + GLYPH_SPACING };
  glyphCache.set(key, trimmed);
  return trimmed;
}

/**
 * Recovering the bitmap costs an offset search, and none of it depends on where the layer sits, so a
 * drag would otherwise pay for it on every frame. Keyed by everything the bitmap is made of.
 */
const bitmapCache = new Map<string, PixelBitmap>();
const BITMAP_CACHE_LIMIT = 24;

function pixelBitmapKey(layer: TextLayer): string {
  return [
    layer.text,
    layer.fontFamily,
    layer.fontWeight,
    layer.edges,
    layer.edgeCutoff,
    layer.strokeWidth,
    layer.monospace,
    paintKey(layer.fill),
    paintKey(layer.stroke),
  ].join(' ');
}

function paintKey(paint: Paint): string {
  return [paint.kind, paint.colour, paint.angle, ...paint.stops.map((s) => `${s.offset}:${s.colour}`)].join(',');
}

/**
 * Where each glyph lands and how wide the run is. Proportional gives every glyph the width its ink
 * needs plus a pixel, the way the game does; monospace keeps the font's fixed cell, which lines
 * columns of text up at the cost of air around narrow glyphs like a dot.
 */
function layoutGlyphs(layer: TextLayer) {
  const bold = layer.fontWeight >= BOLD_FROM ? 1 : 0;
  const cutoff = layer.edges === 'hard' ? layer.edgeCutoff : 0.5;
  const cell = MINECRAFT_GLYPH.advance + bold;
  const fixed = layer.monospace === true;
  const outline = pixelOutlineRings(layer);
  let pen = 0;
  let end = 0;
  const glyphs: { glyph: GlyphMask; at: number }[] = [];
  for (const character of [...layer.text]) {
    const glyph = alignedGlyphMask(character, bold, cutoff);
    glyphs.push({ glyph, at: pen });
    // The air after the last inked glyph only separates it from a next one, so the run ends at its
    // ink plus the outline ring; a trailing blank still counts, the user typed it.
    end = glyph.width > 0 ? pen + glyph.width + outline : pen + (fixed ? cell : glyph.advance);
    pen += fixed ? cell : glyph.advance;
  }
  return { glyphs, width: Math.max(1, end) };
}

/** A one font pixel outline is what a stroke means on this grid, so it is grown from the mask. */
function pixelOutlineRings(layer: TextLayer): number {
  return layer.strokeWidth > 0 && layer.stroke.kind !== 'none' ? Math.round(layer.strokeWidth) : 0;
}

/** Width of the run in font pixels, which the layer box needs and the bitmap must agree with. */
export function pixelTextCells(layer: TextLayer): number {
  return layer.text.length === 0 ? 0 : layoutGlyphs(layer).width;
}

function pixelTextBitmap(layer: TextLayer): PixelBitmap | null {
  const glyphs = [...layer.text];
  if (glyphs.length === 0) return null;

  const key = pixelBitmapKey(layer);
  const cached = bitmapCache.get(key);
  if (cached) return cached;
  const rows = MINECRAFT_GLYPH.height;
  const placed = layoutGlyphs(layer);
  const cells = placed.width;

  // Assembled from per glyph masks: every glyph sits at its own sub pixel offset inside its cell, so
  // one offset for the whole string lands some glyphs half off. A single column stem is then split
  // over two cells and comes out twice as wide or not at all, while fatter glyphs look fine.
  const mask = new Uint8Array(cells * rows);
  for (const { glyph, at } of placed.glyphs) {
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < glyph.width; x++) {
        if (glyph.cells[y * glyph.width + x]) mask[y * cells + at + x] = 1;
      }
    }
  }

  const bitmap = new ImageData(cells, rows);
  for (let p = 0; p < mask.length; p++) {
    bitmap.data[p * 4] = 255;
    bitmap.data[p * 4 + 1] = 255;
    bitmap.data[p * 4 + 2] = 255;
    bitmap.data[p * 4 + 3] = mask[p] ? 255 : 0;
  }
  const outline = pixelOutlineRings(layer);
  if (outline > 0) dilateInto(bitmap, cells, rows, outline);

  if (!tile) tile = document.createElement('canvas');
  const surface = tile;
  const big = surface.getContext('2d', { willReadFrequently: true });
  if (!big) return null;
  surface.width = cells;
  surface.height = rows;
  big.setTransform(1, 0, 0, 1, 0, 0);
  big.globalCompositeOperation = 'source-over';
  big.clearRect(0, 0, cells, rows);
  big.putImageData(bitmap, 0, 0);
  big.globalCompositeOperation = 'source-in';
  big.fillStyle = paintStyle(big, layer.fill, cells, rows) ?? '#ffffff';
  big.fillRect(0, 0, cells, rows);
  big.globalCompositeOperation = 'destination-over';
  if (outline > 0) {
    big.fillStyle = paintStyle(big, layer.stroke, cells, rows) ?? '#000000';
    big.fillRect(0, 0, cells, rows);
  }
  big.globalCompositeOperation = 'source-over';

  // Onto its own surface: the oversized one gets reused by the next layer, and the cache must keep
  // what it was handed.
  const keep = document.createElement('canvas');
  keep.width = cells;
  keep.height = rows;
  keep.getContext('2d')?.drawImage(surface, 0, 0, cells, rows, 0, 0, cells, rows);
  const built: PixelBitmap = { canvas: keep, width: cells, height: rows };
  if (bitmapCache.size >= BITMAP_CACHE_LIMIT) {
    bitmapCache.delete(bitmapCache.keys().next().value as string);
  }
  bitmapCache.set(key, built);
  return built;
}

/**
 * Grows the glyph mask outwards, writing the added ring as opaque black so the caller can paint the
 * ring and the body separately. Marks grown pixels by clearing their colour, not their alpha.
 */
function dilateInto(bitmap: ImageData, width: number, height: number, rings: number) {
  const data = bitmap.data;
  for (let ring = 0; ring < rings; ring++) {
    const before = new Uint8Array(width * height);
    for (let p = 0; p < before.length; p++) before[p] = data[p * 4 + 3];
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const p = y * width + x;
        if (before[p] > 0) continue;
        const near =
          (x > 0 && before[p - 1] > 0) ||
          (x < width - 1 && before[p + 1] > 0) ||
          (y > 0 && before[p - width] > 0) ||
          (y < height - 1 && before[p + width] > 0);
        if (near) data[p * 4 + 3] = 255;
      }
    }
  }
}

function drawPixelText(ctx: CanvasRenderingContext2D, layer: TextLayer) {
  const bitmap = pixelTextBitmap(layer);
  if (!bitmap) return;
  const step = pixelBlockScale(layer);
  const w = Math.round(bitmap.width * step);
  const h = Math.round(bitmap.height * step);
  ctx.save();
  if (layer.rotation === 0) {
    // Whole pixel origin, no centring: the blit must land on the block grid or nearest neighbour
    // splits a one pixel stem across two blocks, which reads as a stem twice as thick or as none.
    ctx.translate(Math.round(layer.x), Math.round(layer.y));
  } else {
    applyTransform(ctx, layer, w, h);
  }
  // Nearest neighbour: the bitmap is already the colour it should be, one pixel per font pixel.
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(bitmap.canvas, 0, 0, bitmap.width, bitmap.height, 0, 0, w, h);
  ctx.restore();
}

function drawText(ctx: CanvasRenderingContext2D, layer: TextLayer) {
  if (isPixelFont(layer.fontFamily)) {
    drawPixelText(ctx, layer);
    return;
  }
  ctx.font = textFont(layer);
  ctx.textBaseline = 'top';
  const bold = pixelBoldOffset(layer);
  const w = textWidth(ctx, layer);
  const h = effectiveFontSize(layer);

  /**
   * Bold on a pixel font draws each glyph twice, a pixel apart, and widens its advance by the same
   * pixel. The game does both; only doubling the draw would run neighbouring letters together.
   */
  const write = (draw: (text: string, x: number) => void) => {
    if (!bold) {
      draw(layer.text, 0);
      return;
    }
    let pen = 0;
    for (const glyph of layer.text) {
      draw(glyph, pen);
      draw(glyph, pen + bold);
      pen += ctx.measureText(glyph).width + bold;
    }
  };

  ctx.save();
  applyTransform(ctx, layer, w, h);
  paintFill(ctx, layer.fill, w, h, () => write((text, x) => ctx.fillText(text, x, 0)));
  paintStroke(ctx, layer, w, h, () => write((text, x) => ctx.strokeText(text, x, 0)));
  ctx.restore();
}

/** Advance of the whole string as drawn, bold's extra pixel per glyph included. */
export function textWidth(ctx: CanvasRenderingContext2D, layer: TextLayer): number {
  if (isPixelFont(layer.fontFamily)) {
    // The pixel path blits a bitmap of whole font pixels, so its width is countable, not measured.
    return Math.round(pixelTextCells(layer) * pixelBlockScale(layer));
  }
  ctx.font = textFont(layer);
  const bold = pixelBoldOffset(layer);
  if (!bold) return ctx.measureText(layer.text).width;
  let width = 0;
  for (const glyph of layer.text) width += ctx.measureText(glyph).width + bold;
  return width;
}

let scratch: HTMLCanvasElement | null = null;

/** One reused layer sized canvas: hard edges need the glyph alone before it lands on the artwork. */
function scratchContext(width: number, height: number): CanvasRenderingContext2D | null {
  if (!scratch) scratch = document.createElement('canvas');
  if (scratch.width !== width || scratch.height !== height) {
    scratch.width = width;
    scratch.height = height;
  }
  const ctx = scratch.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.clearRect(0, 0, width, height);
  return ctx;
}

interface Region {
  x: number;
  y: number;
  width: number;
  height: number;
}

function hardenAlpha(ctx: CanvasRenderingContext2D, region: Region, cutoff: number) {
  if (region.width <= 0 || region.height <= 0) return;
  const image = ctx.getImageData(region.x, region.y, region.width, region.height);
  const threshold = Math.max(1, Math.min(255, Math.round(cutoff * 255)));
  const data = image.data;
  for (let p = 3; p < data.length; p += 4) {
    data[p] = data[p] >= threshold ? 255 : 0;
  }
  ctx.putImageData(image, region.x, region.y);
}

function tracePath(ctx: CanvasRenderingContext2D, layer: ShapeLayer, w: number, h: number) {
  ctx.beginPath();
  if (layer.shape === 'rect') {
    const radius = Math.min(layer.radius * layer.scale, Math.min(w, h) / 2);
    if (radius > 0) ctx.roundRect(0, 0, w, h, radius);
    else ctx.rect(0, 0, w, h);
  } else if (layer.shape === 'ellipse') {
    ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  } else if (layer.shape === 'triangle') {
    ctx.moveTo(w / 2, 0);
    ctx.lineTo(w, h);
    ctx.lineTo(0, h);
    ctx.closePath();
  } else {
    ctx.moveTo(0, h / 2);
    ctx.lineTo(w, h / 2);
  }
}

function paintStyle(
  ctx: CanvasRenderingContext2D,
  paint: Paint,
  w: number,
  h: number,
): string | CanvasGradient | null {
  if (paint.kind === 'none') return null;
  if (paint.kind === 'solid') return paint.colour;

  const stops = paint.stops.length >= 2 ? paint.stops : [{ offset: 0, colour: paint.colour }, { offset: 1, colour: paint.colour }];
  let gradient: CanvasGradient;
  if (paint.kind === 'linear') {
    const radians = (paint.angle * Math.PI) / 180;
    const length = Math.abs(w * Math.cos(radians)) + Math.abs(h * Math.sin(radians));
    const dx = (Math.cos(radians) * length) / 2;
    const dy = (Math.sin(radians) * length) / 2;
    gradient = ctx.createLinearGradient(w / 2 - dx, h / 2 - dy, w / 2 + dx, h / 2 + dy);
  } else {
    gradient = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) / 2);
  }
  for (const stop of stops) {
    gradient.addColorStop(Math.min(1, Math.max(0, stop.offset)), stop.colour);
  }
  return gradient;
}

function paintFill(
  ctx: CanvasRenderingContext2D,
  paint: Paint,
  w: number,
  h: number,
  draw: () => void,
) {
  const style = paintStyle(ctx, paint, w, h);
  if (!style) return;
  ctx.fillStyle = style;
  draw();
}

function paintStroke(
  ctx: CanvasRenderingContext2D,
  layer: TextLayer | ShapeLayer,
  w: number,
  h: number,
  draw: () => void,
) {
  if (layer.strokeWidth <= 0) return;
  const style = paintStyle(ctx, layer.stroke, w, h);
  if (!style) return;
  ctx.strokeStyle = style;
  ctx.lineWidth = layer.strokeWidth * layer.scale;
  ctx.lineJoin = 'round';
  draw();
}

/**
 * A quarter turn about the centre lands on half pixels whenever width and height differ by an odd
 * count, and nearest neighbour then doubles or drops a column. Placing the turned box on whole pixels
 * first keeps every source pixel exactly one square.
 */
function drawOnGrid(ctx: CanvasRenderingContext2D, layer: ImageLayer, prepared: HTMLCanvasElement) {
  const quarters = ((Math.round(layer.rotation / 90) % 4) + 4) % 4;
  const turnedWidth = quarters % 2 === 1 ? prepared.height : prepared.width;
  const turnedHeight = quarters % 2 === 1 ? prepared.width : prepared.height;
  const left = Math.round(layer.x + prepared.width / 2 - turnedWidth / 2);
  const top = Math.round(layer.y + prepared.height / 2 - turnedHeight / 2);
  ctx.imageSmoothingEnabled = false;
  ctx.translate(left + turnedWidth / 2, top + turnedHeight / 2);
  ctx.rotate((quarters * Math.PI) / 2);
  ctx.drawImage(prepared, -prepared.width / 2, -prepared.height / 2);
}

function applyTransform(ctx: CanvasRenderingContext2D, layer: Layer, w: number, h: number) {
  ctx.translate(layer.x + w / 2, layer.y + h / 2);
  ctx.rotate((layer.rotation * Math.PI) / 180);
  ctx.translate(-w / 2, -h / 2);
}

export class History {
  private readonly past: MapartDocument[] = [];
  private readonly future: MapartDocument[] = [];

  push(doc: MapartDocument) {
    this.past.push(structuredClone(doc));
    if (this.past.length > 50) this.past.shift();
    this.future.length = 0;
  }

  undo(current: MapartDocument): MapartDocument | null {
    const previous = this.past.pop();
    if (!previous) return null;
    this.future.push(structuredClone(current));
    return previous;
  }

  redo(current: MapartDocument): MapartDocument | null {
    const next = this.future.pop();
    if (!next) return null;
    this.past.push(structuredClone(current));
    return next;
  }

  reset() {
    this.past.length = 0;
    this.future.length = 0;
  }

  get canUndo() {
    return this.past.length > 0;
  }

  get canRedo() {
    return this.future.length > 0;
  }
}
